import type { ComponentLoader } from '@/shared/registry'

/** What a learner launches, as the API names it. */
export const activityTypes = [
  'lab',
  'scorm',
  'cmi5',
  'ltiLink',
  'externalLink',
  'video',
  'document',
  'assessment',
  'survey',
  'microsoftLearn',
] as const

export type ActivityType = (typeof activityTypes)[number]

/** The grant a launch runs under: an enrollment, a subscription, or a class the caller instructs. */
export type LaunchGrant =
  | { readonly kind: 'enrollment'; readonly enrollmentId: string }
  | { readonly kind: 'subscription'; readonly subscriptionId: string }
  | { readonly kind: 'class'; readonly classId: string }

/** What the activity list offers a person on an activity. */
export const launchActions = ['launch', 'resume', 'retake', 'review'] as const

export type LaunchAction = (typeof launchActions)[number]

/** What every launch button gets. */
export interface LaunchButtonProps {
  /** The activity's public id; for a class's survey slot, the survey's. */
  readonly activityId: string
  readonly grant: LaunchGrant
  /** What the activity list asks the button to do; Launch when not given. */
  readonly action?: LaunchAction
  /** The activity's name, for the button's accessible name and the page it opens. */
  readonly name?: string
  /** Whether the activity opens in a new window, as its author set it. */
  readonly isOpenedInNewWindow?: boolean
  /** For a class's survey slot: the class, and which of its slots. */
  readonly classId?: string
  readonly slot?: 'student' | 'instructor'
  /** Called once the launch went through, so the activity list loads its progress again. */
  readonly onDone?: () => void
}

/** One activity type's entry: the button the activity list and the launch flow show for it. */
export interface LaunchType {
  readonly type: ActivityType
  readonly button: ComponentLoader<LaunchButtonProps>
}

/** The registry: one entry per activity type, and none twice. */
export function buildLaunchTypes(entries: readonly LaunchType[]): ReadonlyMap<ActivityType, LaunchType> {
  const byType = new Map<ActivityType, LaunchType>()
  for (const entry of entries) {
    if (byType.has(entry.type)) {
      throw new Error(`The '${entry.type}' activity type is registered twice.`)
    }
    byType.set(entry.type, entry)
  }
  return byType
}

/** Each adapter adds its own line here, so its button needs nothing else (LCH-04, 06, 12, 13, 16, 19, 21, 23). */
export const launchTypes = buildLaunchTypes([
  // External links and documents open where the launch says, with the activity list's own button.
  { type: 'video', button: () => import('./links/VideoButton').then((module) => module.VideoButton) },
  // A lab launches or resumes in a new window from the list; its name opens its page, which shows its result (LCH-06).
  { type: 'lab', button: () => import('./labs/LabButton').then((module) => module.LabButton) },
  { type: 'scorm', button: () => import('./scorm/ScormButton').then((module) => module.ScormButton) },
  { type: 'cmi5', button: () => import('./cmi5/Cmi5Button').then((module) => module.Cmi5Button) },
  { type: 'ltiLink', button: () => import('./lti/LtiLaunchButton').then((module) => module.LtiLaunchButton) },
  // An assessment opens on its own page, which shows its score once submitted (LCH-19).
  { type: 'assessment', button: () => import('./assessments/AssessmentButton').then((module) => module.AssessmentButton) },
  // A class's survey slot opens the survey page, which completes it on submit (LCH-21).
  { type: 'survey', button: () => import('./surveys/SurveyButton').then((module) => module.SurveyButton) },
])

/** The entry for an activity type, or `undefined` while its adapter is still to come. */
export function findLaunchType(
  type: ActivityType,
  registry: ReadonlyMap<ActivityType, LaunchType> = launchTypes,
): LaunchType | undefined {
  return registry.get(type)
}
